import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { thumbHashToDataURL } from 'thumbhash'
import { useFirebaseUser } from '@/hooks/useFirebaseUser'
import { getStorageDownloadUrl } from '@/integrations/firebase'

type FirebaseStorageImageProps = {
  storagePath: string
  thumbhash?: string
} & React.ImgHTMLAttributes<HTMLImageElement>

const decodeThumbhash = (base64: string): string => {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return thumbHashToDataURL(bytes)
}

const FirebaseStorageImage = ({ storagePath, thumbhash, ...props }: FirebaseStorageImageProps) => {
  const { firebaseUser } = useFirebaseUser()
  const [loaded, setLoaded] = useState(false)
  const query = useQuery({
    queryKey: ['firebaseStorageImage', { storagePath, uid: firebaseUser?.uid }],
    queryFn: () => getStorageDownloadUrl(storagePath),
    gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days
    staleTime: 7 * 24 * 60 * 60 * 1000, // 7 days
    retry: false,
  })

  useEffect(() => {
    setLoaded(false)
  }, [storagePath])

  if (query.error) return undefined

  const placeholder = thumbhash ? decodeThumbhash(thumbhash) : undefined

  return (
    <>
      {placeholder && !loaded && <img alt="" {...props} src={placeholder} />}
      <img
        alt=""
        {...props}
        src={query.data}
        style={{ ...props.style, display: loaded ? undefined : 'none' }}
        onLoad={e => {
          setLoaded(true)
          props.onLoad?.(e)
        }}
      />
    </>
  )
}

export default FirebaseStorageImage
